import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useApp } from '../context/AppContext';
import { useHowItWorksDesktopModal } from '../context/HowItWorksModalContext';
import { FERTILITY_DEMO_HOME } from '../constants/paths';
import LogoWithBeta from './LogoWithBeta';
import { LOGO_HEIGHT_SIDEBAR } from './logoMarkSizes';

// ── Sidebar nav used inside the app ──────────────────────────────────────────
const NAV_ITEMS = [
  { path: '/', icon: '🏠', label: 'Home' },
  { path: '/community', icon: '👥', label: 'Community' },
  { path: '/journey',   icon: '🗺️', label: 'My Journey' },
  { path: '/doctors',   icon: '🏥', label: 'Find a Doctor' },
];

export function AppNav() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, plan, signOut } = useApp();

  return (
    <aside style={{
      width: '220px', minHeight: '100vh', background: 'var(--white)',
      borderRight: '1px solid var(--border)', display: 'flex',
      flexDirection: 'column', padding: '20px 0', flexShrink: 0,
      position: 'sticky', top: 0, height: '100vh',
    }}>
      {/* Logo */}
      <div onClick={() => navigate('/')} role="presentation">
        <LogoWithBeta
          height={LOGO_HEIGHT_SIDEBAR}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '0 20px 20px',
            borderBottom: '1px solid var(--border)',
            marginBottom: 16,
            cursor: 'pointer',
          }}
        />
      </div>

      {/* Nav items */}
      <nav style={{ flex:1, padding:'0 10px', display:'flex', flexDirection:'column', gap:2 }}>
        {NAV_ITEMS.map(item => {
          const active = location.pathname === item.path;
          return (
            <button key={item.path} onClick={() => navigate(item.path)}
              style={{
                display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
                borderRadius:10, border:'none', background: active ? 'var(--pink-l)' : 'none',
                fontSize:13, fontWeight:500, color: active ? 'var(--pink-d)' : 'var(--muted)',
                cursor:'pointer', textAlign:'left', width:'100%', transition:'background .12s',
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--sand)'; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'none'; }}
            >
              <span style={{ width:18, textAlign:'center', fontSize:15 }}>{item.icon}</span>
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* User chip */}
      <div style={{ padding:'16px 10px 0', borderTop:'1px solid var(--border)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:9, padding:'10px 12px', borderRadius:10 }}>
          <div style={{ width:32, height:32, borderRadius:'50%', background:'var(--pink)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:13, fontWeight:700, color:'white', flexShrink:0 }}>
            {user?.name?.[0]?.toUpperCase() || 'M'}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{user?.name || 'Maya'}</div>
            <div style={{ fontSize:11, color:'var(--muted)' }}>
              {plan === 'bloom' ? '🌸 Bloom' : plan === 'roots' ? '🌿 Roots' : '🌱 Free plan'}
            </div>
          </div>
        </div>
        {user && (
          <button
            type="button"
            onClick={() => { signOut(); navigate('/'); }}
            style={{
              margin:'10px 12px 0', width:'calc(100% - 24px)', padding:'8px', fontSize:12, fontWeight:600,
              color:'var(--muted)', background:'var(--sand)', border:'1px solid var(--border)', borderRadius:8, cursor:'pointer',
            }}
          >
            Sign out
          </button>
        )}
      </div>
    </aside>
  );
}

/**
 * Public top nav (after Chapters) — order: Demo · Pricing · About us · Contact
 * Home / landing CTAs: Join the waitlist stays last as primary button
 */
const PUBLIC_TOP_LINKS = [
  { path: FERTILITY_DEMO_HOME, label: 'Demo' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/blog', label: 'Blog' },
  { path: '/about', label: 'About us' },
  { path: '/contact', label: 'Contact' },
];

function isPublicNavLinkActive(pathname, item) {
  if (item.path === '/blog') return pathname === '/blog' || pathname.startsWith('/blog/');
  if (item.path === FERTILITY_DEMO_HOME) return pathname.startsWith('/demo');
  return pathname === item.path;
}

/** Chapters in nav order: Fertility · TTC · Pregnancy · Motherhood */
const CHAPTERS_DROPDOWN = [
  { id: 'fertility', label: 'Fertility', emoji: '🌸' },
  { id: 'ttc', label: 'Trying to Conceive', emoji: '💜' },
  { id: 'pregnancy', label: 'Pregnancy', emoji: '🤰' },
  { id: 'motherhood', label: 'Motherhood', emoji: '🤱' },
];

export function LandingNav({ positionMode = 'sticky', blendWithHero = false } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const compact = useMediaQuery('(max-width: 900px)');
  const { openDesktopOnlyModal } = useHowItWorksDesktopModal();
  const [menuOpen, setMenuOpen] = useState(false);
  const [chaptersOpen, setChaptersOpen] = useState(false);
  const chaptersWrapRef = useRef(null);

  useEffect(() => {
    if (!compact) {
      setMenuOpen(false);
    }
  }, [compact]);

  useEffect(() => {
    if (!chaptersOpen) return;
    const onDown = (e) => {
      if (chaptersWrapRef.current && !chaptersWrapRef.current.contains(e.target)) {
        setChaptersOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [chaptersOpen]);

  const goChapter = (chapterId) => {
    navigate(`/chapter/${chapterId}`);
    setMenuOpen(false);
    setChaptersOpen(false);
  };

  const goPublicLink = (path) => {
    if (path === FERTILITY_DEMO_HOME && compact) {
      openDesktopOnlyModal();
      setMenuOpen(false);
      setChaptersOpen(false);
      return;
    }
    if (path.startsWith('/#')) {
      const hash = path.slice(2);
      navigate('/');
      setMenuOpen(false);
      setChaptersOpen(false);
      if (hash) {
        setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
      }
      return;
    }
    navigate(path);
    setMenuOpen(false);
    setChaptersOpen(false);
  };

  const navSurface = blendWithHero
    ? {
        background: 'rgba(255, 252, 249, 0.4)',
        backdropFilter: 'saturate(1.1) blur(12px)',
        WebkitBackdropFilter: 'saturate(1.1) blur(12px)',
        borderBottom: '1px solid rgba(232, 224, 216, 0.35)',
      }
    : {
        background: 'var(--white)',
        borderBottom: '1px solid var(--border)',
      };

  return (
    <nav style={{
      display: 'flex', flexDirection: 'column', alignItems: 'stretch',
      padding: compact ? '14px 16px' : '18px 48px',
      ...navSurface,
      position: positionMode === 'sticky' ? 'sticky' : 'relative', top: positionMode === 'sticky' ? 0 : undefined, zIndex: positionMode === 'sticky' ? 100 : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div onClick={() => { navigate('/'); setMenuOpen(false); }} role="presentation" style={{ cursor: 'pointer' }}>
          <LogoWithBeta compact={compact} style={{ minWidth: 0 }} />
        </div>

        {compact ? (
          <button
            type="button"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              minWidth: 44, height: 44, padding: '0 14px', borderRadius: 10, border: '1px solid var(--border)',
              background: 'var(--sand)', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: 'var(--text)',
            }}
          >
            {menuOpen ? 'Close' : 'Menu'}
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Chapters — dropdown (desktop) */}
            <div ref={chaptersWrapRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="btn-ghost"
                aria-expanded={chaptersOpen}
                aria-haspopup="listbox"
                id="chapters-menu-button"
                style={{ fontSize: 12, padding: '6px 10px', whiteSpace: 'nowrap' }}
                onClick={() => setChaptersOpen((o) => !o)}
              >
                Chapters
                <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.8 }} aria-hidden>▾</span>
              </button>
              {chaptersOpen && (
                <ul
                  role="listbox"
                  aria-labelledby="chapters-menu-button"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    margin: '8px 0 0',
                    minWidth: 200,
                    padding: '8px 0',
                    listStyle: 'none',
                    background: 'var(--white)',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(17, 17, 17, 0.08)',
                    zIndex: 200,
                  }}
                >
                  {CHAPTERS_DROPDOWN.map((c) => (
                    <li key={c.id} role="option" aria-selected="false">
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => goChapter(c.id)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          fontSize: 13,
                          padding: '10px 16px',
                          borderRadius: 0,
                          color: 'var(--text)',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span aria-hidden>{c.emoji}</span>
                          {c.label}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {PUBLIC_TOP_LINKS.map((item) => {
              const active = isPublicNavLinkActive(location.pathname, item);
              return (
                <button
                  key={item.path + item.label}
                  type="button"
                  className="btn-ghost"
                  style={{
                    fontSize: 12,
                    padding: '6px 10px',
                    whiteSpace: 'nowrap',
                    color: active ? 'var(--pink-d)' : undefined,
                    fontWeight: active ? 700 : undefined,
                    borderBottom: active ? '2px solid var(--pink)' : undefined,
                    borderRadius: active ? 0 : undefined,
                  }}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => goPublicLink(item.path)}
                >
                  {item.label}
                </button>
              );
            })}

            <button className="btn-primary" style={{ padding: '9px 20px', fontSize: 13 }} onClick={() => navigate('/signup?source=nav')}>
              Join the waitlist
            </button>
          </div>
        )}
      </div>

      {compact && menuOpen && (
        <div style={{
          marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          {/* Chapters (mobile menu) */}
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
              padding: '6px 12px 4px',
            }}
          >
            Chapters
          </div>
          {CHAPTERS_DROPDOWN.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => goChapter(c.id)}
              style={{
                textAlign: 'left',
                padding: '14px 12px',
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--text)',
                background: 'var(--sand)',
                border: 'none',
                borderRadius: 10,
                cursor: 'pointer',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span aria-hidden>{c.emoji}</span>
                {c.label}
              </span>
            </button>
          ))}

          {PUBLIC_TOP_LINKS.map((item) => {
            const active = isPublicNavLinkActive(location.pathname, item);
            return (
              <button
                key={item.path + item.label}
                type="button"
                onClick={() => goPublicLink(item.path)}
                style={{
                  textAlign: 'left',
                  padding: '14px 12px',
                  fontSize: 15,
                  fontWeight: active ? 700 : 600,
                  color: active ? 'var(--pink-d)' : 'var(--text)',
                  background: active ? 'var(--pink-l)' : 'var(--sand)',
                  border: 'none',
                  borderRadius: 10,
                  cursor: 'pointer',
                }}
                aria-current={active ? 'page' : undefined}
              >
                {item.label}
              </button>
            );
          })}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <button
              type="button"
              className="btn-primary"
              style={{ width: '100%', padding: '12px', fontSize: 14 }}
              onClick={() => {
                navigate('/signup?source=nav');
                setMenuOpen(false);
              }}
            >
              Join the waitlist
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
